import React, { useState, useMemo, useEffect } from "react";
import "./App.css";
import { Menu } from "lucide-react";
import Logo from "./components/ui/Logo";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Toaster as ShadcnToaster } from "./components/ui/toaster";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";

import { hasGranularPermission } from "./lib/permissions";

// Public route components (hardcoded — outside dynamic menu JSON)
import Dashboard from "./components/Dashboard";
import MyProfilePage from "./components/MyProfilePage";
import LoginPage from "./components/LoginPage";
import ForgotPasswordPage from "./components/ForgotPasswordPage";
import ResetPasswordPage from "./components/ResetPasswordPage";
import SolicitacaoDocumentosPage from "./components/SolicitacaoDocumentosPage";
import MinhaAdmissaoPage from "./components/MinhaAdmissaoPage";
import PublicDISCAssessment from "./components/PublicDISCAssessment";
import PublicCoursePage from "./components/PublicCoursePage";
import ValidarHoleritePage from "./components/ValidarHoleritePage";
import PontoPWATest from "./components/PontoPWATest";
import AvaliacaoPublica from "./components/AvaliacaoPublica";
import AvaliacaoExterna from "./components/profile/fit-cultural/AvaliacaoExterna";
import PublicGridProfileAssessment from "./components/PublicGridProfileAssessment";
import PublicCognitiveAssessment from "./components/PublicCognitiveAssessment";
import PublicEnglishAssessment from "./components/PublicEnglishAssessment";
import PublicExcelAssessment from "./components/PublicExcelAssessment";
import PublicPortugueseAssessment from "./components/PublicPortugueseAssessment";
import PublicSurveyPage from "./components/PublicSurveyPage";
import PublicGridMindResponse from "./components/gridmind/PublicGridMindResponse";
import PublicSignaturePage from "./components/PublicSignaturePage";
import PublicValidationPage from "./components/PublicValidationPage";
import PontoPWAPage from "./components/PontoPWAPage";
import FolhaPontoPWA from "./components/FolhaPontoPWA";
import DocumentosPWA from "./components/DocumentosPWA";
import RegistroViagemPWA from "./components/RegistroViagemPWA";
import AdminViagemPage from "./components/AdminViagemPage";
import EspelhoVisualizarPage from "./components/EspelhoVisualizarPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import SystemFooter from "./components/SystemFooter";

// Component registry for dynamic routes (drawn from menu JSON)
import { componentMap } from "./lib/componentRegistry";

// Rotas extras que nao estao no menu config (sub-paginas navegadas internamente)
const EXTRA_ROUTES = [
  { path: '/historico-viagens/:viagemId', component: 'DetalhesViagemPage' },
  { path: '/avaliacao-wizard', component: 'AvaliacaoDesempenhoWizard' },
  { path: '/minhas-avaliacoes', component: 'MinhasAvaliacoes' },
  { path: '/preencher-avaliacao/:id', component: 'PreencherAvaliacao' },
  { path: '/comparacao-bifocal/:id', component: 'ComparacaoBifocal' },
  { path: '/feedback/cultural', component: 'FeedbackCultural' },
  { path: '/feedback/180/competencias', component: 'Competencias180' },
  { path: '/feedback/90/quick-feedback', component: 'QuickFeedback' },
  { path: '/feedback/90/1on1s', component: 'OneOnOne90' },
  { path: '/feedback/90/1on1s/:id', component: 'OneOnOne90' },
  { path: '/feedback/90/timeline', component: 'FrequencyDashboard90' },
  { path: '/feedback/90/okrs', component: 'FrequencyDashboard90' },
  { path: '/feedback/180/ciclo/novo', component: 'Ciclo180Form' },
  { path: '/feedback/180/ciclo/:id', component: 'Ciclo180Form' },
  { path: '/feedback/180/ciclo/:id/editar', component: 'Ciclo180Form' },
  { path: '/feedback/180/dashboard/:id', component: 'Dashboard180' },
  { path: '/feedback/180/resultado/:id', component: 'Resultado180' },
  { path: '/feedback/180/pdi/:id', component: 'PDI180' },
  { path: '/feedback/180/autoavaliacao/:cicloId', component: 'Autoavaliacao180' },
  { path: '/feedback/180/avaliacao-gestor/:cicloId', component: 'AvaliacaoGestor180' },
  { path: '/feedback/180/calibracao/:cicloId', component: 'Calibracao180' },
  { path: '/feedback/360/selecionar-avaliadores/:id', component: 'SelecionarAvaliadores360' },
  { path: '/feedback/360/dashboard/:id', component: 'Dashboard360' },
  { path: '/feedback/360/view/:id', component: 'Feedback360MainV2' },
  { path: '/feedback/360/edit/:id', component: 'Feedback360MainV2' },
  { path: '/feedback/relatorios', component: 'FeedbackRelatorios' },
  { path: '/9box/configuracao-ciclos', component: 'GerenciarMatrizes9Box' },
  { path: '/9box/estrategias-automaticas', component: 'EstrategiasAutomaticas9Box' },
  { path: '/9box/potencial-enriquecido', component: 'NineBoxPotencialEnriquecido' },
  { path: '/avaliacoes/avancada', component: 'AvaliacaoDesempenhoAvancada' },
  { path: '/minha-autoavaliacao', component: 'MinhaAutoavaliacao' },
  { path: '/meu-perfil', component: 'MyProfilePage' },
  { path: '/feedback/90/frequency-dashboard', component: 'FrequencyDashboard90' },
  { path: '/feedback/180-v2/gerenciar', component: 'GerenciarFeedback180V2' },
  { path: '/feedback/360', component: 'Feedback360MainV2' },
  { path: '/dp-folha/dissidios', component: 'DPFolhaConfigPage', props: { section: 'dissidios' } },
  { path: '/juridico/processos/:id', component: 'GestaoProcessoDetailPage' },
  { path: '/rh/processos/novo', component: 'RHProcessFormPage' },
  { path: '/rh/processos/:id', component: 'RHProcessDetailPage' },
  { path: '/rh/processos/:id/editar', component: 'RHProcessFormPage' },
  { path: '/rh/processos/:id/exame-admissional', component: 'RHAdmissaoExamePage' },
  { path: '/rh/estagiarios/:id', component: 'EstagiarioDetailPage' },
  { path: '/configuracoes/cargos-salarios/upgrade', component: 'SalaryUpgradePage' },
  { path: '/relatorios/graficos', component: 'ChartsReport' },
  { path: '/dcf/relatorios', component: 'ChartsReport' },
  { path: '/dp-folha/funcionarios', component: 'EmployeeList' },
  { path: '/dp-folha/det/arquivadas', component: 'DetArquivadasPage' },
  { path: '/employees/:id', component: 'EmployeeForm' },
  { path: '/profile/fit-cultural/analytics', component: 'DashboardAnalytics' },
  { path: '/profile/fit-cultural/questionario', component: 'GerenciarFitCultural' },
  { path: '/profile/fit-cultural/avaliacoes', component: 'MinhasAvaliacoesFitCultural' },
  { path: '/profile/fit-cultural/valores', component: 'ConfiguracaoAvaliacao' },
  { path: '/profile/identidade-organizacional', component: 'IdentidadeOrganizacional' },
  { path: '/documentos/funcionarios/:id', component: 'DocumentosFuncionarioPage' },
  { path: '/admin/pins', component: 'PinManagerPage' },
];

// Hooks
// Components used in AppLayout
import Navigation from "./components/Navigation";
import MenuEditorPage from "./components/MenuEditorPage";
import SystemConfigPage from "./components/SystemConfigPage";
import MayaChat from "./components/MayaChat";
import CommandPalette from "./components/CommandPalette";
import { MayaProvider } from "./contexts/MayaContext";
import SessionManager from "./components/SessionManager";

// ─── Guard components ────────────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const redirectTo = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirectTo)}`}
        replace
      />
    );
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { isAdmin, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const temPermissaoModulo = (user, isAdmin, moduleId) => {
  if (isAdmin || user?.is_admin) return true;
  // NEW: granted module-level access when using permitted_ids (tab-level check handles restriction)
  if (user?.permitted_ids?.length > 0) return true;
  // LEGACY
  if (!user?.granular_permissions || user.granular_permissions.length === 0) {
    return user?.menu_permissions?.includes(moduleId) || false;
  }
  const mod = user.granular_permissions.find((m) => m.module_id === moduleId);
  return !!(mod && mod.enabled);
};

const ModuleRoute = ({ moduleId, tabId, children }) => {
  const { user, isAdmin, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const redirectTo = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirectTo)}`}
        replace
      />
    );
  }

  if (!temPermissaoModulo(user, isAdmin, moduleId)) {
    return <Navigate to="/" replace />;
  }

  if (tabId && !hasGranularPermission(user, moduleId, tabId)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// ─── App Layout ──────────────────────────────────────────────────
const AppLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Navigation
        onCollapsedChange={setSidebarCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <div
        className={`transition-all duration-300 pl-0 ${sidebarCollapsed ? "md:pl-16" : "md:pl-64"}`}
      >
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 h-14 px-4 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Abrir menu"
            aria-expanded={mobileOpen}
            aria-controls="app-sidebar"
          >
            <Menu className="h-6 w-6" />
          </button>
          <Logo
            variant="full"
            theme="light"
            iconClassName="h-7 w-auto"
            wordClassName="text-base"
          />
        </div>

        <main className="p-4 md:p-6">{children}</main>
      </div>
      <MayaChat />
      <CommandPalette />
    </div>
  );
};

// ─── Helper: flatten menu tree into flat route array ──────────────
const flattenRoutes = (items) => {
  const routes = [];
  const walk = (list) => {
    for (const item of list) {
      if (item.path && item.component) {
        routes.push(item);
      }
      if (item.children) {
        walk(item.children);
      }
    }
  };
  walk(items);
  return routes;
};

// ─── App Routes ──────────────────────────────────────────────────
const isSafeInternalRedirect = (target) => {
  return typeof target === "string" && target.startsWith("/") && !target.startsWith("//");
};

const AppRoutes = () => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const postLoginTarget =
    redirectParam && isSafeInternalRedirect(redirectParam) ? redirectParam : "/";

  const flatRoutes = useMemo(() => {
    if (user?.navigation?.items) {
      return flattenRoutes(user.navigation.items);
    }
    return [];
  }, [user?.navigation?.items]);

  return (
    <Routes>
      {/* ============ PUBLIC ROUTES ============ */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to={postLoginTarget} replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
      <Route path="/redefinir-senha/:token" element={<ResetPasswordPage />} />
      <Route
        path="/documentos/solicitar/:token"
        element={<SolicitacaoDocumentosPage />}
      />
      <Route
        path="/minha-admissao/:token"
        element={<MinhaAdmissaoPage />}
      />
      <Route
        path="/disc/public/:token"
        element={<PublicDISCAssessment />}
      />
      <Route path="/curso/:token" element={<PublicCoursePage />} />
      <Route
        path="/validar-holerite/:hash"
        element={<ValidarHoleritePage />}
      />
      <Route path="/ponto/test" element={<PontoPWATest />} />
      <Route
        path="/politica-de-privacidade/:policyId"
        element={<PrivacyPolicyPage />}
      />
      <Route
        path="/privacy-policy/:policyId"
        element={<PrivacyPolicyPage />}
      />
      <Route
        path="/minha-avaliacao/:token"
        element={<AvaliacaoPublica />}
      />
      <Route
        path="/avaliacao-externa/:token"
        element={<AvaliacaoExterna />}
      />
      <Route
        path="/gridprofile/public/:token"
        element={<PublicGridProfileAssessment />}
      />
      <Route
        path="/cognitive/public/:token"
        element={<PublicCognitiveAssessment />}
      />
      <Route
        path="/english/public/:token"
        element={<PublicEnglishAssessment />}
      />
      <Route
        path="/excel/public/:token"
        element={<PublicExcelAssessment />}
      />
      <Route
        path="/portuguese/public/:token"
        element={<PublicPortugueseAssessment />}
      />
      <Route
        path="/survey/public/:token"
        element={<PublicSurveyPage />}
      />
      <Route
        path="/gridmind/responder/:token"
        element={<PublicGridMindResponse />}
      />
      <Route
        path="/assinatura/:token"
        element={<PublicSignaturePage />}
      />
      <Route
        path="/assinatura-publica/:token"
        element={<PublicSignaturePage />}
      />
      <Route
        path="/validate/:validationCode?"
        element={<PublicValidationPage />}
      />
      <Route
        path="/espelho/:token"
        element={<EspelhoVisualizarPage />}
      />
      <Route path="/ponto/mobile" element={<PontoPWAPage />} />
      <Route path="/pwa" element={<PontoPWAPage />} />
      <Route
        path="/ponto-pwa/folha-ponto"
        element={<FolhaPontoPWA />}
      />
      <Route
        path="/ponto-pwa/documentos"
        element={<DocumentosPWA />}
      />
      <Route
        path="/registro-viagem"
        element={<RegistroViagemPWA />}
      />
      <Route path="/admin-viagem" element={<AdminViagemPage />} />

      {/* Protected route without sidebar layout */}
      <Route
        path="/ponto/mobile-protected"
        element={
          <ProtectedRoute>
            <PontoPWAPage />
          </ProtectedRoute>
        }
      />

      {/* Hardcoded admin routes */}
      <Route
        path="/admin/menu-editor"
        element={
          <AdminRoute>
            <AppLayout>
              <MenuEditorPage />
            </AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/configuracoes-sistema"
        element={
          <AdminRoute>
            <AppLayout>
              <SystemConfigPage />
            </AppLayout>
          </AdminRoute>
        }
      />

      {/* Hardcoded redirects */}
      <Route
        path="/dp-folha/integracao-gridrecruter"
        element={
          <Navigate to="/avaliacoes/integracao-gridrecruter" replace />
        }
      />

      {/* ============ DYNAMIC ROUTES FROM MENU JSON ============ */}
      {/* Home is always available as fallback */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {flatRoutes
        .filter((item) => item.path !== "/")
        // Deduplicar por pathname (ignorando query string)
        .filter((item, index, arr) => {
          const base = item.path.split("?")[0];
          return index === arr.findIndex((i) => i.path.split("?")[0] === base);
        })
        .map((item) => {
          const Component = componentMap[item.component];
          if (!Component) return null;
          const cleanPath = item.path.split("?")[0];

          const content = (
            <AppLayout>
              <Component {...item.props} />
            </AppLayout>
          );

          if (item.guard === "admin") {
            return (
              <Route
                key={item.id || item.path}
                path={cleanPath}
                element={<AdminRoute>{content}</AdminRoute>}
              />
            );
          }

          if (item.guard === "module") {
            return (
              <Route
                key={item.id || item.path}
                path={cleanPath}
                element={
                  <ModuleRoute
                    moduleId={item.moduleId}
                    tabId={item.tabId}
                  >
                    {content}
                  </ModuleRoute>
                }
              />
            );
          }

          return (
            <Route
              key={item.id || item.path}
              path={cleanPath}
              element={<ProtectedRoute>{content}</ProtectedRoute>}
            />
          );
        })}

      {/* Extra routes (sub-paginas fora do menu config) */}
      {EXTRA_ROUTES.map((route) => {
        const Component = componentMap[route.component];
        if (!Component) return null;
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Component />
                </AppLayout>
              </ProtectedRoute>
            }
          />
        );
      })}

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// ─── Root App ────────────────────────────────────────────────────
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <MayaProvider>
            <div className="app-shell min-h-screen flex flex-col">
              <SessionManager />
              <div className="flex-1">
                <AppRoutes />
              </div>
              <SystemFooter />
              <Toaster />
              <ShadcnToaster />
            </div>
          </MayaProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
